import { Routes, Route, useParams, useLocation } from "react-router-dom";
import { AppNavLink, AppNavigate } from "./lib/route-primitives";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { MobileBottomNav, type MobileNavItem } from "@wedding/ui";
import type { RoutePath } from "@wedding/shared";
import { useOrganizerApi } from "./hooks/use-organizer-api";
import { cn } from "./lib/utils";
import { LanguageSwitcher } from "./components/language-switcher";
import { UserMenu } from "./components/user-menu";
import {
  OrganizerAuthProvider,
  useOrganizerAuth,
} from "./contexts/organizer-auth-context";
import { WeddingProvider, useWedding } from "./contexts/wedding-context";
import { formatWeddingDate } from "@wedding/shared";
import { useActiveEvent } from "@wedding/ui";
import { OrganizerLoginPage } from "./pages/organizer-login";
import { RejiPage } from "./pages/reji";
import { paths, routePatterns } from "./lib/paths";
import {
  ORGANIZER_GLOBAL_ROUTES,
  ORGANIZER_WEDDING_ROUTES,
  ORGANIZER_WEDDING_LAYOUT_ROUTES,
  type OrganizerWeddingRoute,
} from "./lib/nav-registry";
import { buildRouteElements } from "./lib/build-routes";
import { useTheme } from "./hooks/use-theme";
import { ArrowLeft, Loader2, Sun, Moon } from "lucide-react";

// ─── Scroll to top on every route change ──────────────────────
import { useEffect } from "react";
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    // Use rAF to ensure the scroll reset runs after the browser has painted
    const id = requestAnimationFrame(() => {
      window.scrollTo(0, 0);
      const el = document.getElementById("content-scroll");
      if (el) el.scrollTop = 0;
    });
    return () => cancelAnimationFrame(id);
  }, [pathname]);
  return null;
}

// ─── Theme toggle (pill switch) ──────────────────────────────
function ThemeToggle({ isDark, toggle }: { isDark: boolean; toggle: () => void }) {
  const { t } = useTranslation();
  const label = isDark ? t("common.switchToLight") : t("common.switchToDark");
  return (
    <button
      onClick={toggle}
      role="switch"
      aria-checked={isDark}
      aria-label={label}
      title={label}
      className={cn(
        "relative h-6 w-11 rounded-full border-2 transition-[background-color,border-color,opacity,transform] duration-300 cursor-pointer shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 hover:opacity-80 hover:scale-105 motion-reduce:hover:scale-100",
        isDark ? "bg-primary/30 border-primary/50" : "bg-amber-200 border-amber-300"
      )}
    >
      <span
        className={cn(
          "absolute top-0.5 h-4 w-4 rounded-full shadow-sm transition-transform duration-300 flex items-center justify-center",
          isDark ? "translate-x-[18px] bg-primary" : "translate-x-0.5 bg-amber-400"
        )}
      >
        {isDark
          ? <Moon className="h-2.5 w-2.5 text-white" />
          : <Sun className="h-2.5 w-2.5 text-white" />
        }
      </span>
    </button>
  );
}

/** Gate: login only at root — any other path redirects to / first */
function AuthGate({ children }: { children: React.ReactNode }) {
  const { organizer, isLoading } = useOrganizerAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!organizer) {
    // Not authenticated: login at root, signup at /signup (bilgi e-postasındaki bağlantı).
    // Any other path → redirect to / (no login form on arbitrary URLs)
    if (location.pathname === paths.signup) return <OrganizerLoginPage mode="signup" />;
    if (location.pathname !== "/") {
      return <AppNavigate to={paths.root} replace />;
    }
    return <OrganizerLoginPage />;
  }

  // Oturum açıkken kayıt sayfası anlamsız ("signup" ayrılmış slug — düğün değil).
  if (location.pathname === paths.signup) return <AppNavigate to={paths.root} replace />;

  return <>{children}</>;
}

/** Gate: checks if wedding slug exists before showing anything */
function WeddingGate({ children }: { children: React.ReactNode }) {
  const { isLoading, error, wedding } = useWedding();

  // Only block on first load; subsequent refreshes keep children mounted
  if (isLoading && !wedding) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    // Wedding doesn't exist (404) or organizer has no access (403)
    // Redirect to wedding list — authorization denied at API layer
    return <AppNavigate to={paths.root} replace />;
  }

  return <>{children}</>;
}

/** Reji: full-bleed route outside WeddingLayout — needs its own provider + gates. */
function RejiRoute() {
  return (
    <WeddingProvider>
      <WeddingGate>
        <RejiFeatureGate>
          <RejiPage />
        </RejiFeatureGate>
      </WeddingGate>
    </WeddingProvider>
  );
}

/** Redirect to the dashboard when the slideshow feature is disabled. */
function RejiFeatureGate({ children }: { children: React.ReactNode }) {
  const { slug, wedding } = useWedding();
  const features = (wedding?.config?.features ?? {}) as { slideshow?: boolean };
  if (features.slideshow === false) {
    return <AppNavigate to={paths.wedding.dashboard(slug ?? "")} replace />;
  }
  return <>{children}</>;
}

/** Per-wedding layout with sidebar navigation */
function WeddingLayout() {
  const { slug } = useParams<{ slug: string }>();
  // Nav TÜM düğün rotalarından türer — reji dahil, çünkü reji nav'da görünür
  // ama rotası layout'un dışında (bkz. nav-registry `outsideLayout`).
  const navItems: NavEntry[] = ORGANIZER_WEDDING_ROUTES.map((r) => ({
    to: r.to(slug ?? ""),
    icon: r.icon,
    labelKey: r.labelKey,
    end: r.end,
    showsPendingBadge: r.showsPendingBadge,
  }));

  return (
    <WeddingProvider>
      <WeddingGate>
        <WeddingLayoutInner navItems={navItems} />
      </WeddingGate>
    </WeddingProvider>
  );
}

type NavEntry = Pick<OrganizerWeddingRoute, "icon" | "labelKey" | "end" | "showsPendingBadge"> & {
  /** `r.to(slug)` sonucu — marka tipi burada da korunur, aksi halde
   *  `MobileNavItem.to` ve `AppNavLink` sınırında düz `string`e düşerdi. */
  to: RoutePath;
};

function WeddingLayoutInner({ navItems }: { navItems: NavEntry[] }) {
  const { t, i18n } = useTranslation();
  const { wedding, slug } = useWedding();
  // Kabuktaki tarih satırı en yakın etkinliği gösterir (kına → sonra düğün).
  const activeEvent = useActiveEvent(wedding);
  const { logout, organizer } = useOrganizerAuth();
  const { isDark, toggle: toggleTheme } = useTheme();

  // Persistent moderation notice: a toast/Telegram ping is missable — this
  // badge stays on the nav until the pending queue is actually handled.
  const api = useOrganizerApi();
  const { data: pendingCount = 0 } = useQuery({
    queryKey: ["organizer", slug, "pending-count"],
    queryFn: async () => {
      const res = await api.get<{ media?: { pending?: number } }>("/stats");
      return res.data?.media?.pending ?? 0;
    },
    refetchInterval: 60_000, // light poll + refetch-on-focus (query default)
  });

  const PendingBadge = ({ className }: { className?: string }) =>
    pendingCount > 0 ? (
      <span
        className={cn(
          "min-w-5 h-5 px-1.5 rounded-full bg-primary text-primary-foreground text-[11px] font-bold flex items-center justify-center tabular-nums",
          className,
        )}
      >
        {pendingCount > 99 ? "99+" : pendingCount}
      </span>
    ) : null;

  // Bottom bar shows the first 4; the rest go to the "More" sheet. Labels are
  // translated here because the shared component takes ready-to-render strings.
  // Moderation carries the pending badge and is deliberately in the visible 4.
  const mobileNavItems: MobileNavItem[] = navItems.map((item) => ({
    to: item.to,
    icon: item.icon,
    label: t(item.labelKey),
    end: item.end,
    badge: item.showsPendingBadge ? pendingCount : undefined,
  }));

  return (
    <div className="relative min-h-screen md:flex md:overflow-hidden md:h-screen">

      {/* ─── Desktop Sidebar ───────────────────────────── */}
      <aside className="hidden md:flex w-64 shrink-0 flex-col border-r border-primary/10 bg-background sticky top-0 h-screen overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-7 border-b border-primary/10">
          <AppNavLink
            to={paths.root}
            className="mb-3 flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors"
          >
            <ArrowLeft className="h-3 w-3" /> {t("nav.myWeddings")}
          </AppNavLink>
          {wedding?.title ? (
            <>
              <p className="font-serif text-2xl font-bold text-foreground leading-tight">
                {wedding.title}
              </p>
              {wedding.date_time && (
                <p className="mt-1.5 text-xs font-medium text-primary/70 tracking-wide">
                  {new Date(wedding.date_time).toLocaleDateString(i18n.language, {
                    year: "numeric", month: "long", day: "numeric",
                  })}
                </p>
              )}
            </>
          ) : (
            <p className="text-lg font-bold text-foreground truncate">
              Vite
            </p>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <AppNavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium transition-colors",
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-primary/5 hover:text-primary"
                )
              }
            >
              <item.icon className="h-4 w-4 shrink-0" />
              {t(item.labelKey)}
              {item.showsPendingBadge && <PendingBadge className="ml-auto" />}
            </AppNavLink>
          ))}
        </nav>

      </aside>

      {/* ─── Mobile Fixed Header ───────────────────────── */}
      <header className="glass fixed top-0 left-0 right-0 z-50 h-16 border-b border-primary/10 md:hidden">
        <div className="flex items-center justify-between h-full px-4">
          <AppNavLink
            to={paths.root}
            className="p-2 text-muted-foreground hover:text-primary transition-colors shrink-0"
            aria-label={t("nav.myWeddings")}
          >
            <ArrowLeft className="h-5 w-5" />
          </AppNavLink>
          <h1 className="font-serif text-xl italic font-bold text-primary truncate mx-3">
            {wedding?.title ?? "Vite"}
          </h1>
          <div className="flex items-center gap-1 shrink-0">
            <ThemeToggle isDark={isDark} toggle={toggleTheme} />
            <UserMenu />
          </div>
        </div>
      </header>

      {/* ─── Desktop sticky header + content ──────────── */}
      <div id="content-scroll" className="flex-1 flex flex-col md:overflow-y-auto md:h-screen">
        <header className="hidden md:flex sticky top-0 z-10 items-center justify-end px-8 py-4 bg-background/80 backdrop-blur-md border-b border-primary/5 shrink-0 gap-2">
          <ThemeToggle isDark={isDark} toggle={toggleTheme} />
          <UserMenu />
        </header>

        <main className="flex-1 px-4 pt-20 pb-24 md:px-8 md:py-8 overflow-x-clip">
          <div className="mx-auto w-full max-w-screen-md md:max-w-7xl">
            <ScrollToTop />
            <Routes>
              {buildRouteElements(ORGANIZER_WEDDING_LAYOUT_ROUTES, (r) => <r.element />)}
              <Route path="*" element={<AppNavigate to={paths.self} replace />} />
            </Routes>
          </div>
        </main>
      </div>

      {/* ─── Mobile Bottom Tab Bar (shared: 4 + "More" sheet) ─────── */}
      <MobileBottomNav navItems={mobileNavItems} />
    </div>
  );
}

/**
 * Organizer App — email OTP-authenticated organizer panel.
 * Login via e-mail + OTP code.
 * After login, organizer sees their assigned weddings.
 */
export function App() {
  return (
    <OrganizerAuthProvider>
      <AuthGate>
        <Routes>
          {buildRouteElements(ORGANIZER_GLOBAL_ROUTES, (r) => <r.element />)}
          {/* Full-bleed director console — top-level sibling: escapes the
              wedding chrome, so it carries its own WeddingProvider. Must stay
              BEFORE the /:slug/* catch-all. Path + nav label live in
              nav-registry (`outsideLayout: true`); only the provider/gate
              wrapper stays here. */}
          <Route path={routePatterns.weddingReji} element={<RejiRoute />} />
          <Route path={routePatterns.weddingCatchAll} element={<WeddingLayout />} />
          <Route path="*" element={<AppNavigate to={paths.root} replace />} />
        </Routes>
      </AuthGate>
    </OrganizerAuthProvider>
  );
}
